import OutlineButtonLinkComponent from '@components/buttons/OutlineButtonLink';
import PrimaryButtonLinkComponent from '@components/buttons/PrimaryButtonLink';
import CheckmarkIconComponent from '@components/icons/checkmarkIcon';
import InfoCircleOutlineIconComponent from '@components/icons/infoCircleOutlineIcon';
import LoadingIconComponent from '@components/icons/loadingIcon';
import MaxSubscriptionIcon from '@components/icons/maxSubscriptionIcon';
import MinusIconComponent from '@components/icons/minus';
import {
  BodyTextSmall,
  Heading4,
  Heading5,
  Label2,
} from '@components/web-landingpage/Texts';
import { PricingPageSubscriptionInterval } from '@pages/pro';
import {
  getActiveSectionDetailsUrl,
  getRedirectFallbackToActiveSectionUrl,
  getRegisterPageLink,
} from '@utils/link-utils-web';
import { DefaultTooltip } from '@utils/tooltip-utils';
import { useRouter } from 'next/router';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { twMerge } from 'tailwind-merge';
import { PlanComparisonState } from './Pricing.Page';
import { getSpecialDiscountOfferCouponForCheckout } from '@utils/special-offer-time-utils';
import {
  mobileAppTableData,
  webPlatformTableData,
} from '../subscribe/DetailedComparisonTable';
import { PercentOffBadge, StrikeThroughPrice } from './PlanComparison';

export function DetailedComparisonTableComponent({
  planComparisonState,
  interval,
  id,
  className,
}: {
  planComparisonState: PlanComparisonState;
  interval: PricingPageSubscriptionInterval;
  id?: string;
  className?: string;
}) {
  const router = useRouter();
  const { couponId } = router.query;

  // FIXME: to prevent discrepancies, we are removing this display of the recurring discount from the pricing page for the ab test period
  // const specialOfferWindow = isUserInSpecialDiscountOfferWindow();
  const specialOfferWindow: any = undefined;

  return (
    <div id={id} className={twMerge('', className)}>
      <ReactTooltip
        className="defaultTooltip"
        noArrow
        id="comparison-table-tooltip"
        place="right"
      ></ReactTooltip>
      <section
        className={`sticky z-[1] grid grid-cols-4 bg-product2-background-primary pt-4 ${specialOfferWindow ? 'top-[120px]' : 'top-[81px]'}`}
      >
        <Heading4 className="break-word self-end p-6 pr-1">
          Feature comparison
        </Heading4>
        <div className="flex flex-col px-4 py-6 lg:px-6">
          <Label2>Basic</Label2>
          <Heading5 className="mt-4">Free</Heading5>
          <BodyTextSmall className="text-product2-content-secondary">
            Forever
          </BodyTextSmall>
          <OutlineButtonLinkComponent
            className="mt-5 min-h-[40px] py-1"
            href={getRegisterPageLink()}
          >
            Start learning
          </OutlineButtonLinkComponent>
        </div>
        <div className="flex flex-col px-4 py-6 lg:px-6">
          <Label2>Pro</Label2>
          {planComparisonState.type ===
          'error' ? null : planComparisonState.type === 'loading' ? (
            <div className="mt-4 flex h-8 items-center">
              <LoadingIconComponent className="h-6 w-6 animate-spin"></LoadingIconComponent>
            </div>
          ) : (
            <Heading5 className="mt-4">
              {interval === 'monthly'
                ? planComparisonState.data.stripePricesPro.monthlyPrice
                : planComparisonState.data.stripePricesPro.yearlyPricePerMonth}
            </Heading5>
          )}
          <BodyTextSmall className="text-product2-content-secondary">
            /month {interval === 'annually' && 'billed yearly'}
          </BodyTextSmall>
          <PrimaryButtonLinkComponent
            className="mt-5 min-h-[40px] py-1"
            href={{
              pathname: '/go-to-checkout',
              query: {
                interval,
                ...((couponId
                  ? { couponId }
                  : {
                      ...(specialOfferWindow
                        ? {
                            couponId:
                              getSpecialDiscountOfferCouponForCheckout(),
                          }
                        : {}),
                    }) satisfies { couponId?: string | string[] }),
                cancelUrl:
                  getActiveSectionDetailsUrl() ||
                  getRedirectFallbackToActiveSectionUrl(),
                upgradeType: 'pro',
              },
            }}
          >
            {interval === 'annually' ? 'Start 7-day free trial' : 'Subscribe'}
          </PrimaryButtonLinkComponent>
        </div>
        <div className="relative flex flex-col rounded-t-3xl border-x-2 border-t-2 border-product2-border-accent px-4 py-6 lg:px-6">
          <div className="flex h-[22px] items-center">
            <MaxSubscriptionIcon className="h-[14px] w-[52px]"></MaxSubscriptionIcon>
          </div>
          <PercentOffBadge
            specialOfferWindow={specialOfferWindow}
          ></PercentOffBadge>
          {planComparisonState.type ===
          'error' ? null : planComparisonState.type === 'loading' ? (
            <div className="mt-4 flex h-8 items-center">
              <LoadingIconComponent className="h-6 w-6 animate-spin"></LoadingIconComponent>
            </div>
          ) : (
            <div className="flex items-baseline space-x-2 text-product2-content-primary">
              <Heading5 className="mt-4">
                {!specialOfferWindow
                  ? interval === 'monthly'
                    ? planComparisonState.data.stripePricesMax.monthlyPrice
                    : planComparisonState.data.stripePricesMax
                        .yearlyPricePerMonth
                  : interval === 'monthly'
                    ? planComparisonState.data.stripePricesMax
                        .monthlyPriceWithDiscount
                    : planComparisonState.data.stripePricesMax
                        .yearlyPriceWithDiscountPerMonth}
              </Heading5>
              {specialOfferWindow && (
                <StrikeThroughPrice
                  monthlyPrice={
                    planComparisonState.data.stripePricesMax.monthlyPrice
                  }
                  yearlyPricePerMonth={
                    planComparisonState.data.stripePricesMax.yearlyPricePerMonth
                  }
                  interval={interval}
                  className="text-base"
                ></StrikeThroughPrice>
              )}
            </div>
          )}
          <BodyTextSmall className="text-product2-content-secondary">
            /month {interval === 'annually' && 'billed yearly'}
          </BodyTextSmall>
          <PrimaryButtonLinkComponent
            className="mt-5 min-h-[40px] py-1"
            href={{
              pathname: '/go-to-checkout',
              query: {
                interval,
                ...(couponId ? { couponId } : {}),
                cancelUrl:
                  getActiveSectionDetailsUrl() ||
                  getRedirectFallbackToActiveSectionUrl(),
                upgradeType: 'max',
              },
            }}
          >
            {interval === 'annually' ? 'Start 7-day free trial' : 'Subscribe'}
          </PrimaryButtonLinkComponent>
        </div>
      </section>
      <div className="relative top-20 -mt-20 rounded-3xl border-2 border-product2-border-secondary pb-4">
        <Heading5 className="col-span-4 mb-4 rounded-t-[22px] bg-product2-background-secondary p-6 text-product2-content-primary">
          Mobile App
        </Heading5>
        {mobileAppTableData.map((data, index) => (
          <ComparisonTableRowComponent
            data={data}
            key={index}
          ></ComparisonTableRowComponent>
        ))}
        <Heading5 className="col-span-4 my-4 bg-product2-background-secondary p-6 text-product2-content-primary">
          Web Platform
        </Heading5>
        {webPlatformTableData.map((data, index) => (
          <ComparisonTableRowComponent
            data={data}
            key={index}
          ></ComparisonTableRowComponent>
        ))}
        <div className="absolute -bottom-[2px] -right-[2px] h-[calc(100%+4px)] w-[calc(25%+1px)] rounded-b-3xl border-x-2 border-b-2 border-product2-border-accent bg-transparent p-6"></div>
      </div>
    </div>
  );
}

function ComparisonTableRowComponent({
  data,
}: {
  data: ComparisonTableRowData;
}) {
  return (
    <div className="flex py-4">
      <div className="ml-6 flex flex-1 items-center space-x-2">
        <BodyTextSmall>{data.title}</BodyTextSmall>
        <InfoCircleOutlineIconComponent
          className="h-4 w-4 cursor-pointer"
          data-tooltip-id="comparison-table-tooltip"
          data-tooltip-html={DefaultTooltip(data.tooltip, 'max-w-[200px]')}
        ></InfoCircleOutlineIconComponent>
      </div>
      {typeof data.basic === 'string' || typeof data.basic === 'number' ? (
        <BodyTextSmall className="flex-1 text-center">
          {data.basic}
        </BodyTextSmall>
      ) : data.basic ? (
        <CheckmarkIconComponent className="h-6 w-6 flex-1"></CheckmarkIconComponent>
      ) : (
        <MinusIconComponent className="h-6 w-6 flex-1"></MinusIconComponent>
      )}
      {typeof data.pro === 'string' || typeof data.pro === 'number' ? (
        <BodyTextSmall className="flex-1 text-center">{data.pro}</BodyTextSmall>
      ) : data.pro ? (
        <CheckmarkIconComponent className="h-6 w-6 flex-1"></CheckmarkIconComponent>
      ) : (
        <MinusIconComponent className="h-6 w-6 flex-1"></MinusIconComponent>
      )}
      {typeof data.max === 'string' || typeof data.max === 'number' ? (
        <BodyTextSmall className="flex-1 text-center">{data.max}</BodyTextSmall>
      ) : data.max ? (
        <CheckmarkIconComponent className="h-6 w-6 flex-1"></CheckmarkIconComponent>
      ) : (
        <MinusIconComponent className="h-6 w-6 flex-1"></MinusIconComponent>
      )}
    </div>
  );
}

interface ComparisonTableRowData {
  title: string;
  basic: string | number | boolean;
  pro: string | number | boolean;
  max: string | number | boolean;
  tooltip: string;
}
